import React from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, MagnifyingGlass } from "@phosphor-icons/react";
import { fetcher } from "./common";

export default function ChartBrowser({ file }) {
  const [q, setQ] = React.useState("");
  const { data: accounts = [], isLoading } = useSWR(`/accounting/files/${file.id}/accounts?q=${encodeURIComponent(q)}`, fetcher);
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ code: "", name: "" });

  const addCustom = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/accounting/files/${file.id}/accounts`, form);
      toast.success("Compte ajouté au plan du dossier");
      setOpen(false);
      setForm({ code: "", name: "" });
      mutate(`/accounting/files/${file.id}/accounts?q=${encodeURIComponent(q)}`);
      mutate(`/accounting/files/${file.id}/accounts`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Ajout impossible"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative w-full sm:w-96">
          <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-zinc-400" />
          <Input className="pl-9" placeholder="Rechercher par code ou intitulé (ex : 411, client, banque…)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="chart-search" />
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="add-custom-account-btn"><Plus size={16} className="mr-1" /> Compte personnalisé</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">Compte personnalisé</DialogTitle>
              <DialogDescription>Complète le plan {file.chart_label} pour ce dossier (ex : compte auxiliaire client 4111DUPONT).</DialogDescription>
            </DialogHeader>
            <form onSubmit={addCustom} className="space-y-3">
              <div><Label>Code (2 à 8 chiffres)</Label><Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="custom-account-code" placeholder="4111001" /></div>
              <div><Label>Intitulé</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="custom-account-name" placeholder="Client — SARL Dupont" /></div>
              <div className="flex justify-end"><Button type="submit" className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="custom-account-submit">Ajouter</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
        <div className="px-4 py-2 border-b border-zinc-200 text-xs text-zinc-500">{accounts.length} compte(s) — {file.chart_label}</div>
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-zinc-100">
              {isLoading && <tr><td className="p-6 text-zinc-500">Chargement…</td></tr>}
              {accounts.map((a) => (
                <tr key={a.code} className="hover:bg-zinc-50" data-testid={`account-row-${a.code}`}>
                  <td className="px-4 py-2 font-mono text-xs w-28">{a.code}</td>
                  <td className="px-4 py-2">{a.name}</td>
                  <td className="px-4 py-2 text-right">{a.custom && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">Personnalisé</span>}</td>
                </tr>
              ))}
              {!isLoading && accounts.length === 0 && <tr><td className="p-6 text-zinc-500">Aucun compte trouvé.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
